import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/** Una operacion del historial, venga de la tabla que venga. */
export interface FilaHistorial {
  fuente: 'TRANSACCION' | 'WALLET_USDT' | 'WALLET_VES' | 'BALANCE';
  id: string;
  referencia: string | null;
  fecha: string | null;
  fecha_creacion: string | null;
  fecha_termino: string | null;
  tipo: string | null;
  grupo: string;
  rail: string | null;
  estado: string;
  usuario_id: number | null;
  email: string | null;
  nombre: string | null;
  documento: string | null;
  tipo_usuario: string | null;
  moneda_origen: string | null;
  monto_origen: number | null;
  moneda_destino: string | null;
  monto_destino: number | null;
  tasa: number | null;
  beneficiario: string | null;
  banco: string | null;
  cuenta: string | null;
  operador: string | null;
  motivo_rechazo: string | null;
  vita_tracking_id: string | null;
  vita_status: string | null;
}

export interface PaginaHistorial {
  datos: FilaHistorial[];
  pagina: number;
  limite: number;
  total: number;
  totalPaginas: number;
  /** El periodo que el backend realmente consulto (puede diferir del pedido). */
  desde: string;
  hasta: string;
  desdeUtc: string | null;
  hastaUtc: string | null;
  /**
   * Cantidad y monto de TODO el resultado filtrado, no solo de la pagina.
   * Opcional: un backend anterior no lo manda, y la pantalla se arregla sin el.
   */
  totales?: TotalHistorial[];
}

/** Un renglon de los totales: una combinacion de grupo, tipo, moneda y estado. */
export interface TotalHistorial {
  grupo: string;
  tipo: string | null;
  moneda: string | null;
  estado: string;
  cantidad: number;
  /** Suma de `monto_origen`, que siempre es positivo. */
  monto: number;
}

export interface CatalogosHistorial {
  grupos: string[];
  estados: string[];
  fuentes: string[];
  tipos: string[];
  monedas: string[];
}

/** Lo que el componente manda; todo opcional. */
export interface FiltrosHistorial {
  desde?: string;
  hasta?: string;
  usuario?: string;
  operador?: string;
  tipos?: string[];
  grupos?: string[];
  estados?: string[];
  fuentes?: string[];
  moneda?: string;
  baseFecha?: 'movimiento' | 'termino';
  orden?: 'asc' | 'desc';
  pagina?: number;
  limite?: number;
}

@Injectable({ providedIn: 'root' })
export class OperationsHistoryService {
  private readonly apiUrl = `${environment.apiNest}operations-history`;

  constructor(private http: HttpClient) {}

  private getHeaders(): HttpHeaders {
    return new HttpHeaders().set(
      'Authorization',
      `Bearer ${localStorage.getItem('token') || ''}`,
    );
  }

  /** Filtros -> query string. Las listas viajan separadas por coma. */
  private aParams(filtros: FiltrosHistorial): HttpParams {
    let params = new HttpParams();
    const poner = (clave: string, valor: unknown) => {
      if (valor === undefined || valor === null || valor === '') return;
      if (Array.isArray(valor)) {
        if (valor.length === 0) return;
        params = params.set(clave, valor.join(','));
        return;
      }
      params = params.set(clave, String(valor));
    };

    poner('desde', filtros.desde);
    poner('hasta', filtros.hasta);
    poner('usuario', filtros.usuario);
    poner('operador', filtros.operador);
    poner('tipos', filtros.tipos);
    poner('grupos', filtros.grupos);
    poner('estados', filtros.estados);
    poner('fuentes', filtros.fuentes);
    poner('moneda', filtros.moneda);
    poner('baseFecha', filtros.baseFecha);
    poner('orden', filtros.orden);
    poner('pagina', filtros.pagina);
    poner('limite', filtros.limite);
    return params;
  }

  listar(filtros: FiltrosHistorial): Observable<PaginaHistorial> {
    return this.http.get<PaginaHistorial>(this.apiUrl, {
      headers: this.getHeaders(),
      params: this.aParams(filtros),
    });
  }

  catalogos(): Observable<CatalogosHistorial> {
    return this.http.get<CatalogosHistorial>(`${this.apiUrl}/catalogos`, {
      headers: this.getHeaders(),
    });
  }

  /**
   * Descarga el archivo con los filtros que se estan viendo.
   *
   * No es un `<a href>` porque el endpoint exige el token en la cabecera y un
   * link del navegador no manda cabeceras. Se pide el blob y se dispara la
   * descarga a mano. Mismo patron que `ReporteClienteService`.
   */
  async descargar(
    filtros: FiltrosHistorial,
    formato: 'csv' | 'xlsx',
  ): Promise<void> {
    const params = this.aParams({ ...filtros, pagina: undefined, limite: undefined })
      .set('formato', formato);

    const respuesta = await firstValueFrom(
      this.http.get(`${this.apiUrl}/export`, {
        headers: this.getHeaders(),
        params,
        responseType: 'blob',
        observe: 'response',
      }),
    );
    this.guardarArchivo(respuesta, formato);
  }

  private guardarArchivo(
    respuesta: HttpResponse<Blob>,
    formato: string,
  ): void {
    const blob = respuesta.body;
    if (!blob) throw new Error('La respuesta llegó vacía.');

    const enlace = document.createElement('a');
    const url = URL.createObjectURL(blob);
    enlace.href = url;
    enlace.download = this.nombreDeArchivo(respuesta, formato);
    document.body.appendChild(enlace);
    enlace.click();
    document.body.removeChild(enlace);

    // Se revoca despues, no en este tick: revocar justo detras del `click()`
    // llega a cancelar la descarga y sale un archivo de 0 bytes.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  private nombreDeArchivo(
    respuesta: HttpResponse<Blob>,
    formato: string,
  ): string {
    const cabecera = respuesta.headers.get('Content-Disposition') ?? '';
    const conComillas = /filename="([^"]+)"/i.exec(cabecera);
    if (conComillas?.[1]) return conComillas[1];
    const sinComillas = /filename=([^;]+)/i.exec(cabecera);
    if (sinComillas?.[1]) return sinComillas[1].trim();
    return `historial-operaciones_${new Date().toISOString().slice(0, 10)}.${formato}`;
  }

  /**
   * El mensaje que escribio el backend.
   *
   * LA TRAMPA: como la peticion pidio `responseType: 'blob'`, el cuerpo de una
   * respuesta de ERROR tambien llega como Blob. Leerlo directo da
   * `[object Blob]` y el operador ve "Error" en vez de "son 80.000 filas,
   * acota el periodo".
   */
  async mensajeDeError(error: unknown): Promise<string> {
    if (!(error instanceof HttpErrorResponse)) {
      return 'No se pudo generar el archivo. Intentá de nuevo.';
    }
    if (error.status === 0) {
      return 'Se interrumpió la conexión. Revisá tu internet e intentá de nuevo.';
    }

    let crudo = '';
    const cuerpo = error.error;
    if (typeof cuerpo === 'string') crudo = cuerpo;
    else if (cuerpo instanceof Blob) {
      try {
        crudo = await cuerpo.text();
      } catch {
        crudo = '';
      }
    } else if (cuerpo && typeof cuerpo === 'object') {
      crudo = JSON.stringify(cuerpo);
    }

    try {
      const json = JSON.parse(crudo);
      const mensaje = json?.message ?? json?.error;
      if (typeof mensaje === 'string' && mensaje.trim()) return mensaje;
      if (Array.isArray(mensaje) && mensaje.length) return String(mensaje[0]);
    } catch {
      if (crudo.trim() && !crudo.trim().startsWith('<')) return crudo.trim();
    }

    if (error.status === 401) return 'Tu sesión expiró. Volvé a iniciar sesión.';
    if (error.status === 403) return 'No tenés permisos de operador.';
    return 'No se pudo generar el archivo. Intentá de nuevo.';
  }
}
